import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, Globe, Mail, MapPin, Phone } from 'lucide-react'
import { buttonStyles } from '@/lib/button-styles'
import { Logo } from '@/components/generals/logo'
import { WhatsappIcon } from '@/components/generals/whatsapp-icon'

import {
    contact,
    defaultWhatsappMessage,
    navLinks,
    siteConfig,
    whatsappLink
} from '@/constants/site'

const orderLinks = [
    { label: 'Book Catering', href: '/book' },
    { label: 'Order From Abroad', href: '/order-from-abroad' },
    { label: 'Our Story', href: '/about' },
    { label: 'Browse the Menu', href: '/#menu' }
]

export const Footer: FC = (): ReactNode => {
    return (
        <footer className='relative overflow-clip bg-brand-ink text-brand-mist'>
            <div aria-hidden='true' className='pointer-events-none absolute -top-40 -right-40 size-[28rem] rounded-full bg-brand-orange/10 blur-3xl' />

            <div className='relative border-b border-white/10'>
                <div className='mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-[1.4fr_1fr] lg:items-end lg:px-10 lg:py-20'>
                    <div className='reveal'>
                        <p className='inline-flex items-center gap-3 text-[0.72rem] font-semibold tracking-[0.3em] text-brand-gold uppercase'>
                            <span aria-hidden='true' className='h-px w-8 bg-brand-gold/70' />
                            Let&apos;s plan your menu
                        </p>

                        <h2 className='mt-5 font-heading text-[2.6rem] leading-[1.02] font-semibold tracking-tight text-brand-white sm:text-5xl lg:text-6xl'>
                            Planning something special?{' '}
                            <em className='text-brand-orange'>Let&apos;s make it delicious.</em>
                        </h2>
                    </div>

                    <div className='reveal flex flex-wrap gap-3 lg:justify-end'>
                        <Link href='/book' className={buttonStyles({ variant: 'primary', size: 'lg', className: 'focus-visible:ring-offset-brand-ink' })}>
                            Book Your Catering
                            <ArrowRight className='size-4 transition-transform duration-300 group-hover/button:translate-x-1' aria-hidden='true' />
                        </Link>

                        <a
                            href={whatsappLink(defaultWhatsappMessage)}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={buttonStyles({ variant: 'outline-light', size: 'lg', className: 'focus-visible:ring-offset-brand-ink' })}
                        >
                            <WhatsappIcon className='size-4' />
                            Chat on WhatsApp
                        </a>
                    </div>
                </div>
            </div>

            <div className='relative mx-auto grid max-w-7xl gap-12 px-6 py-14 sm:grid-cols-2 lg:grid-cols-[1.35fr_0.75fr_0.85fr_1.05fr] lg:px-10'>
                <div className='space-y-5'>
                    <Link href='/' aria-label='Loveth Tasty Global, back to the home page' className='inline-block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange'>
                        <Logo tone='dark' size='lg' />
                    </Link>

                    <p className='max-w-sm text-sm leading-relaxed'>
                        Premium Nigerian and international catering from Akure, for weddings, celebrations, corporate events and the loved ones you miss from abroad.
                    </p>

                    <p className='font-heading text-xl text-brand-gold italic'>
                        {siteConfig.tagline}.
                    </p>
                </div>

                <nav aria-label='Footer'>
                    <h3 className='text-xs font-semibold tracking-[0.25em] text-brand-white uppercase'>
                        Explore
                    </h3>

                    <ul className='mt-5 space-y-3 text-sm'>
                        {navLinks.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='transition-colors hover:text-brand-white'>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div>
                    <h3 className='text-xs font-semibold tracking-[0.25em] text-brand-white uppercase'>
                        Order and Book
                    </h3>

                    <ul className='mt-5 space-y-3 text-sm'>
                        {orderLinks.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='transition-colors hover:text-brand-white'>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h3 className='text-xs font-semibold tracking-[0.25em] text-brand-white uppercase'>
                        Get in Touch
                    </h3>

                    <ul className='mt-5 space-y-4 text-sm'>
                        <li>
                            <a href={contact.phoneHref} className='group flex items-start gap-3 transition-colors hover:text-brand-white'>
                                <Phone className='mt-0.5 size-4 shrink-0 text-brand-gold' aria-hidden='true' />
                                <span>
                                    {contact.phoneDisplay}
                                    <span className='block text-xs text-brand-mist/80'>
                                        {contact.phoneInternational} from abroad
                                    </span>
                                </span>
                            </a>
                        </li>

                        <li>
                            <a
                                href={whatsappLink(defaultWhatsappMessage)}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='flex items-start gap-3 transition-colors hover:text-brand-white'
                            >
                                <WhatsappIcon className='mt-0.5 size-4 shrink-0 text-brand-gold' />
                                Chat on WhatsApp
                            </a>
                        </li>

                        <li>
                            <a href={`mailto:${contact.email}`} className='flex items-start gap-3 break-all transition-colors hover:text-brand-white'>
                                <Mail className='mt-0.5 size-4 shrink-0 text-brand-gold' aria-hidden='true' />
                                {contact.email}
                            </a>
                        </li>

                        <li>
                            <a
                                href={contact.mapsHref}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='flex items-start gap-3 transition-colors hover:text-brand-white'
                            >
                                <MapPin className='mt-0.5 size-4 shrink-0 text-brand-gold' aria-hidden='true' />
                                {contact.location}
                            </a>
                        </li>

                        <li className='flex items-start gap-3'>
                            <Globe className='mt-0.5 size-4 shrink-0 text-brand-gold' aria-hidden='true' />
                            Orders from the UK, USA, Canada and worldwide
                        </li>
                    </ul>
                </div>
            </div>

            <div className='relative border-t border-white/10'>
                <div className='mx-auto flex max-w-7xl flex-col gap-2 px-6 py-6 text-xs sm:flex-row sm:items-center sm:justify-between lg:px-10'>
                    <p>
                        &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> Loveth Tasty Global. All rights reserved.
                    </p>

                    <p>
                        From Our Kitchen To The World &ndash; Local &amp; International Delicacies
                    </p>
                </div>
            </div>
        </footer>
    )
}
